import React from 'react';
import {
  AlertTriangle, ArrowRight, BrainCircuit, CheckCircle2, Clock3, Import, ShieldCheck,
  Sparkles, TrendingUp, UserRoundCheck, Users,
} from 'lucide-react';
import { Avatar, Badge, Button, Confidence, EmptyState, PageHeader, ScoreRing, SectionHeader, StatusBadge } from '../components/ui.jsx';
import { formatDate, formatTaskMessage, getCandidateConfidence, getCandidateScore, workflowStatusLabel, workflowStatusTone } from '../utils.js';

const workflowColors = {
  needs_review: 'var(--amber-500)', backup: 'var(--ink-400)', recommended: 'var(--green-600)',
  scheduling: 'var(--teal-500)', interview_scheduled: 'var(--blue-600)', not_match: 'var(--red-600)',
};

function MetricCard({ icon: Icon, label, value, meta, tone, bars }) {
  return (
    <article className={`metric-card metric-${tone}`}>
      <div className="metric-top"><span><Icon size={18} /></span><small>{label}</small></div>
      <div className="metric-value"><strong>{value ?? '—'}</strong>{meta && <span>{meta}</span>}</div>
      <div className="micro-bars" aria-hidden="true">
        {(bars || [31, 46, 38, 62, 54, 75, 82, 74, 91]).map((height, index) => <i key={index} style={{ height: `${height}%` }} />)}
      </div>
    </article>
  );
}

function CandidateRow({ candidate, index, navigate }) {
  const score = getCandidateScore(candidate);
  return (
    <button className="candidate-mini-row" onClick={() => navigate('candidate', candidate.id)}>
      <span className="rank-number">{String(index + 1).padStart(2, '0')}</span>
      <Avatar name={candidate.displayName} tone={index} />
      <span className="candidate-mini-main">
        <strong>{candidate.displayName}</strong>
        <small>{candidate.potential?.level || '潜力待评估'} · {candidate.evidenceCount} 条证据</small>
      </span>
      <Badge tone={workflowStatusTone(candidate.workflowStatus)}>{workflowStatusLabel(candidate.workflowStatus)}</Badge>
      <ScoreRing score={score} size={44} />
      <ArrowRight size={16} />
    </button>
  );
}

export default function OverviewPage({ state, navigate }) {
  const dashboard = state?.dashboard || {};
  const currentUser = state?.currentUser;
  const workflowCounts = dashboard.workflowCounts || {};
  const workflowTotal = Math.max(1, Object.values(workflowCounts).reduce((sum, value) => sum + value, 0));
  const activeTasks = state?.tasks?.filter((task) => ['queued', 'processing'].includes(task.status)) || [];
  const roleTitle = currentUser?.role === 'hiring_manager' ? '我的招聘待办' : currentUser?.role === 'hr' ? '招聘运营工作台' : '招聘全局总览';
  const roleDescription = currentUser?.role === 'hiring_manager'
    ? `${state?.jobs?.length || 0} 个岗位 · 优先处理复核与面试反馈`
    : `${state?.jobs?.length || 0} 个岗位 · 更新于 ${formatDate(new Date().toISOString(), true)}`;

  return (
    <div className="page overview-page">
      <PageHeader
        eyebrow={`${state?.workflow?.roleLabels?.[currentUser?.role] || '招聘工作台'} · ${currentUser?.name || ''}`}
        title={roleTitle}
        description={roleDescription}
        actions={<Button icon={Import} variant="primary" onClick={() => navigate('import')}>导入候选人</Button>}
      />

      <section className="metrics-grid">
        <MetricCard icon={Users} label="候选人" value={dashboard.candidateCount} meta={`${dashboard.scoredCount || 0} 人已完成 AI 评估`} tone="green" />
        <MetricCard icon={AlertTriangle} label="需复核" value={workflowCounts.needs_review || 0} meta="等待招聘官判断" tone="amber" bars={[73, 62, 84, 56, 70, 48, 38, 45, 32]} />
        <MetricCard icon={UserRoundCheck} label="备选 / 推荐" value={(workflowCounts.backup || 0) + (workflowCounts.recommended || 0)} meta={`${workflowCounts.recommended || 0} 人已推荐面试`} tone="blue" bars={[42, 55, 48, 61, 69, 66, 74, 81, 78]} />
        <MetricCard icon={Clock3} label="约面 / 待面试" value={(workflowCounts.scheduling || 0) + (workflowCounts.interview_scheduled || 0)} meta={`${workflowCounts.interview_scheduled || 0} 场已确认`} tone="coral" bars={[28, 34, 42, 45, 58, 64, 61, 79, 86]} />
      </section>

      <section className="overview-main-grid">
        <div className="panel top-candidates-panel">
          <SectionHeader
            title="优先查看"
            description="综合分、证据置信度与人工调整后的当前排序"
            action={<button className="text-action" onClick={() => navigate('candidates')}>完整排名 <ArrowRight size={15} /></button>}
          />
          <div className="candidate-mini-list">
            {(dashboard.topCandidates || []).map((candidate, index) => (
              <CandidateRow key={candidate.id} candidate={candidate} index={index} navigate={navigate} />
            ))}
          </div>
        </div>

        <div className="panel distribution-panel">
          <SectionHeader title="招聘阶段分布" description={`${workflowTotal} 位候选人的人工流程状态`} />
          <div className="distribution-total">
            <div className="distribution-donut" style={{ '--strong': `${(workflowCounts.recommended || 0) / workflowTotal * 100}%`, '--recommended': `${((workflowCounts.recommended || 0) + (workflowCounts.interview_scheduled || 0)) / workflowTotal * 100}%` }}>
              <span><strong>{dashboard.candidateCount || 0}</strong><small>候选人</small></span>
            </div>
          </div>
          <div className="distribution-list">
            {['needs_review', 'backup', 'recommended', 'scheduling', 'interview_scheduled', 'not_match'].map((status) => (
              <div key={status}>
                <span><i style={{ background: workflowColors[status] }} />{workflowStatusLabel(status)}</span>
                <div><i style={{ width: `${(workflowCounts[status] || 0) / workflowTotal * 100}%`, background: workflowColors[status] }} /></div>
                <strong>{workflowCounts[status] || 0}</strong>
              </div>
            ))}
          </div>
          <div className="fairness-line"><ShieldCheck size={16} /><span>AI 建议与人工招聘阶段相互独立</span></div>
        </div>
      </section>

      <section className="overview-secondary-grid">
        <div className="panel review-panel">
          <SectionHeader
            title="需要判断的事项"
            description="按风险强度和置信度排序"
            action={<Badge tone="warning">{dashboard.reviewCount || 0} 项待处理</Badge>}
          />
          {(dashboard.reviewQueue || []).length === 0 ? (
            <EmptyState icon={CheckCircle2} title="复核队列已清空" />
          ) : (
            <div className="review-list">
              {dashboard.reviewQueue.map((candidate, index) => (
                <button key={candidate.id} onClick={() => navigate('candidate', candidate.id)}>
                  <span className={`risk-marker risk-${candidate.risks?.[0]?.severity || 'low'}`}><AlertTriangle size={16} /></span>
                  <span><strong>{candidate.displayName}</strong><small>{candidate.risks?.[0]?.title || '证据待复核'}</small></span>
                  <Confidence value={getCandidateConfidence(candidate)} compact />
                  <ArrowRight size={15} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="panel potential-panel">
          <SectionHeader title="潜力信号" description="识别学习速度，不被当前年资掩盖" />
          {state?.candidates?.filter((candidate) => candidate.potential?.score >= 88).slice(0, 3).map((candidate, index) => (
            <button key={candidate.id} onClick={() => navigate('candidate', candidate.id)} className="potential-row">
              <div className="potential-score"><BrainCircuit size={17} /><strong>{candidate.potential.score}</strong></div>
              <div><strong>{candidate.displayName}</strong><p>{candidate.potential.signals?.slice(0, 2).join(' · ')}</p></div>
              <ArrowRight size={15} />
            </button>
          ))}
          <button className="pattern-library-link" onClick={() => navigate('talent-library')}>
            <Sparkles size={17} /><span>查看优秀人才特征库</span><ArrowRight size={15} />
          </button>
        </div>

        <div className="panel queue-panel">
          <SectionHeader title="处理流水线" description={activeTasks.length ? `${activeTasks.length} 个任务正在运行` : '当前处理服务空闲'} />
          {activeTasks.length ? activeTasks.slice(0, 3).map((task) => (
            <div className="queue-task-row" key={task.id}>
              <span className="queue-icon"><Clock3 size={16} /></span>
              <div><strong>{task.candidateName || '候选人处理'}</strong><small>{formatTaskMessage(task.message)}</small><div><i style={{ width: `${task.progress}%` }} /></div></div>
              <strong>{task.progress}%</strong>
            </div>
          )) : (
            <div className="queue-idle"><UserRoundCheck size={25} /><div><strong>系统已就绪</strong><span>新导入材料将进入可恢复任务队列</span></div></div>
          )}
          <div className="pipeline-steps">
            {['归档', '文本层', '渲染', 'OCR', '简历复核', '评分'].map((step, index) => <span key={step}><i>{index + 1}</i>{step}</span>)}
          </div>
        </div>
      </section>
    </div>
  );
}
